import {useDispatch, useSelector} from 'react-redux';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {omneticApi} from '@dms/api/base';
import {datagridApi, type KanbanSortBy} from '@dms/api/core';
import {selectActiveBranchId} from '@dms/api/features';
import {BusinessCaseType} from '@dms/api/sales';
import {notificationTypes, useIsCommunicationReadStateEnabled, useLazyDataQuery} from '@dms/shared';

import {useDebouncedCallback} from 'shared/web';

import {getKanbanOrder} from '../utils/getKanbanOrder';

const UNREAD_REFRESH_DEBOUNCE_MS = 500;

export const useKanbanControl = (
  businessCaseType: BusinessCaseType,
  setBusinessCaseType: (businessCaseType: BusinessCaseType) => void
) => {
  const dispatch = useDispatch();
  const branchId = useSelector(selectActiveBranchId);
  const isReadStateEnabled = useIsCommunicationReadStateEnabled();

  // Requested only with the flags on: a grid that does not know a column rejects the whole query.
  const unreadColumns = isReadStateEnabled ? ['unreadMessageCount'] : [];

  // The cards poll once an hour, so the unread counts follow the read state pushes instead.
  // Debounced because one bulk change pushes one notification per affected message.
  const refreshUnreadCounts = useDebouncedCallback(() => {
    dispatch(
      omneticApi.util.invalidateTags([
        {type: 'DataGrid', id: 'business-case'},
        {type: 'DataGrid', id: 'interest'},
      ])
    );
  }, UNREAD_REFRESH_DEBOUNCE_MS);

  useSubscribeToNotificationsQuery((notification) => {
    if (
      !isReadStateEnabled ||
      notification.type !== notificationTypes.COMMUNICATION_UNREAD_CHANGED
    ) {
      return;
    }

    refreshUnreadCounts();
  });

  const [
    getBusinessCaseQueryParams,
    {
      data: businessCaseQuery,
      isLoading: isLoadingBusinessCaseQuery,
      isFetching: isFetchingBusinessCaseQuery,
      isError: isLoadingBusinessCaseQueryErrored,
    },
  ] = useLazyDataQuery('business-case');

  const [
    getInterestQueryParams,
    {
      data: interestQuery,
      isLoading: isLoadingInterestQuery,
      isFetching: isFetchingInterestQuery,
      isError: isLoadingInterestQueryErrored,
    },
  ] = useLazyDataQuery('interest');

  const {
    data: businessCases,
    isLoading: isLoadingBusinessCases,
    isFetching: isFetchingBusinessCases,
    isError: isLoadingBusinessCasesErrored,
  } = datagridApi.useGetDataByDataQueryQuery(
    {
      gridCode: 'business-case',
      // eslint-disable-next-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
      dataQueryId: businessCaseQuery?.dataQueryId!,
      offset: 0,
      limit: 1000,
      body: {
        columns: [
          'businessCaseState',
          'vehicle',
          'customerName',
          'tag',
          'comment',
          'note',
          'businessCasePrice',
          'vehiclePriceWithVat',
          'businessCaseUpdatedAt',
          'businessCaseCreatedAt',
          'lastActivityDate',
          'lastCommunicationDate',
          'lastCommunicationChannel',
          'owner',
          ...unreadColumns,
        ],
      },
    },
    {
      skip: isNilOrEmpty(businessCaseQuery?.dataQueryId),
      refetchOnMountOrArgChange: true,
      pollingInterval: KANBAN_POLLING_INTERVAL,
    }
  );

  const {
    data: interests,
    isLoading: isLoadingInterests,
    isFetching: isFetchingInterests,
    isError: isLoadingInterestsErrored,
  } = datagridApi.useGetDataByDataQueryQuery(
    {
      gridCode: 'interest',
      // eslint-disable-next-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
      dataQueryId: interestQuery?.dataQueryId!,
      offset: 0,
      limit: 1000,
      body: {
        columns: [
          'state',
          'type',
          'customer',
          'tag',
          'owner',
          'createdAt',
          'updatedAt',
          'comment',
          'note',
          'vehicle',
          'lastActivityDate',
          'lastCommunicationDate',
          'lastCommunicationChannel',
          ...unreadColumns,
        ],
      },
    },
    {
      skip: isNilOrEmpty(interestQuery?.dataQueryId),
      refetchOnMountOrArgChange: true,
      pollingInterval: KANBAN_POLLING_INTERVAL,
    }
  );

  const onFilterChange = async (
    nextBusinessCaseType: BusinessCaseType,
    filteredUserIds: string[],
    sortBy: KanbanSortBy
  ) => {
    if (isNilOrEmpty(filteredUserIds)) {
      return;
    }

    await getBusinessCaseQueryParams({
      filters: {
        owner: filteredUserIds,
        businessCaseType: [nextBusinessCaseType],
        businessCaseState: [
          'OFFER',
          'INVOICE',
          'CONCEPT',
          'CONTRACT',
          'DEPOSIT',
          'DEPOSIT_PAID',
          'PAID',
        ],
        branch: [branchId],
      },
      order: getKanbanOrder(sortBy, 'business-case'),
      smartSearch: null,
    });

    await getInterestQueryParams({
      filters: {
        owner: filteredUserIds,
        type: [nextBusinessCaseType],
        state: ['NEW', 'OPEN'],
        branch: [branchId],
      },
      order: getKanbanOrder(sortBy, 'interest'),
      smartSearch: null,
    });

    setBusinessCaseType(nextBusinessCaseType);
  };

  return {
    businessCaseType,
    businessCases,
    interests,
    isLoading:
      isLoadingInterestQuery ||
      isLoadingBusinessCaseQuery ||
      isLoadingBusinessCases ||
      isLoadingInterests,
    isFetching:
      isFetchingBusinessCaseQuery ||
      isFetchingInterestQuery ||
      isFetchingBusinessCases ||
      isFetchingInterests,
    isError:
      isLoadingInterestQueryErrored ||
      isLoadingBusinessCaseQueryErrored ||
      isLoadingBusinessCasesErrored ||
      isLoadingInterestsErrored,
    onFilterChange,
  };
};

/**
 * Polling interval for updates in milliseconds (60 minutes)
 * This is used to refresh the data in the kanban
 * This is needed because the kanban is not updated when the data is updated
 * and the data is not updated in the kanban when the user is not in the kanban
 *
 */
const KANBAN_POLLING_INTERVAL = 60 * 60 * 1000;
